import React, { useState, useEffect, useMemo } from 'react';
import { WeeklyTicketVolume } from './WeeklyTicketVolume';
import { DailyTicketVolume } from './DailyTicketVolume';
import { HourlyTicketChart } from './HourlyTicketChart';
import { DeploymentImpactTimeline } from './DeploymentImpactTimeline';
import { RepetitiveTicketsChart } from './RepetitiveTicketsChart';
import { useRepetitiveTickets } from '@/hooks/useRepetitiveTickets';
import { DashboardStats } from './DashboardStats';
import { DeploymentIncidentList } from './incidents/DeploymentIncidentList';
import { LinkedIncidentsTable } from './incidents/LinkedIncidentsTable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown, AlertTriangle, Calendar as CalendarIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExecutiveSummary } from './ExecutiveSummary';
import { ResourceAllocationChart } from './ResourceAllocationChart';
import { RootCauseAnalysis } from './RootCauseAnalysis';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PredictiveAnalysis } from './PredictiveAnalysis';
import { DateRange } from 'react-day-picker';
import { parseISO, subDays, format } from 'date-fns';
import { isDeploymentTask } from '../../utils/deploymentProcessing';
import { DeploymentPatternAnalysis } from '../../components/dashboard/DeploymentPatternAnalysis';
import { PredictiveFeatures } from '../../components/dashboard/PredictiveFeatures';
import { AIPredictiveAnalysis } from './AIPredictiveAnalysis';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { WeekdayWeekendAnalytics } from './WeekdayWeekendAnalytics';
import { HourlyDistributionAnalytics } from './HourlyDistributionAnalytics';
import { AvgResolutionTime } from './AvgResolutionTime';


interface OverviewProps {
  filteredData: { [key: string]: any[] };
  excelData: { [key: string]: any[] };
  stats: {
    totalIncidents: number;
    totalCatalogTasks: number;
    linkedIncidents: number;
    highPriorityIncidents: number;
    repetitiveTickets: number;
    openTickets: number;
    inProgressTickets: number;
    closedTickets: number;
    resolvedTickets: number;
  };
  dataSource?: 'initial' | 'static' | 'empty' | 'json';
  dateRange?: DateRange;
}

export function Overview({
  filteredData,
  excelData,
  stats,
  dataSource = 'static',
  dateRange
}: OverviewProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("executive");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedSingleDate, setSelectedSingleDate] = useState<Date>(new Date());
  const [showHourlyChart, setShowHourlyChart] = useState(false);

  
  const safeFilteredData = React.useMemo(() => {
    const result = {
      'Incidents': Array.isArray(filteredData['Incidents']) ? filteredData['Incidents'].filter(Boolean) : [],
      'Catalog Tasks': Array.isArray(filteredData['Catalog Tasks']) ? filteredData['Catalog Tasks'].filter(Boolean) : []
    };
    console.log("Overview component processed filtered data:", {
      incidents: result['Incidents'].length,
      catalogTasks: result['Catalog Tasks'].length,
      dataSource: dataSource
    });
    return result;
  }, [filteredData, dataSource]);
  
  useEffect(() => {
    console.log("Overview component received data:");
    console.log("Filtered data:", safeFilteredData);
    console.log("Incidents count:", safeFilteredData['Incidents']?.length || 0);
    console.log("Catalog Tasks count:", safeFilteredData['Catalog Tasks']?.length || 0);
    console.log("Data source:", dataSource);
    
    if (safeFilteredData['Incidents']?.length > 0) {
      console.log("Sample incident:", safeFilteredData['Incidents'][0]);
    }
    if (safeFilteredData['Catalog Tasks']?.length > 0) {
      console.log("Sample catalog task:", safeFilteredData['Catalog Tasks'][0]);
    }
    
    const hasData = (safeFilteredData['Incidents']?.length || 0) + (safeFilteredData['Catalog Tasks']?.length || 0) > 0;
    
    if (hasData) {
      setDataLoaded(true);
      toast.success(`Loaded ${safeFilteredData['Incidents']?.length || 0} incidents and ${safeFilteredData['Catalog Tasks']?.length || 0} catalog tasks`);
    } else if (!dataLoaded) {
      toast.warning("No data available or could not be loaded");
    }
  }, [safeFilteredData, dataLoaded, dataSource]);
  
  const incidents = safeFilteredData['Incidents'] || [];
  const catalogTasks = safeFilteredData['Catalog Tasks'] || [];

  function filterDeploymentCatalogTasks(tasks: any[]) {
    return tasks.filter(task => isDeploymentTask(task));
  }

  const deploymentCatalogTasks = React.useMemo(() => {
    return filterDeploymentCatalogTasks(catalogTasks);
  }, [catalogTasks]);

  const repetitiveTicketsData = useRepetitiveTickets(incidents, catalogTasks);
  console.log("Repetitive tickets data:", repetitiveTicketsData);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (!dataLoaded) {
      timer = setTimeout(() => {
        if ((incidents.length === 0) && (catalogTasks.length === 0)) {
          toast.info("If no data appears, check the JSON files format");
        }
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [incidents.length, catalogTasks.length, dataLoaded]);
  
  // Process data based on what's available
  const displayIncidents = useMemo(() => {
    const safeFilteredData = filteredData || {};
    return Array.isArray(safeFilteredData['Incidents']) 
      ? safeFilteredData['Incidents'].filter(Boolean)
      : [];
  }, [filteredData]);

  const displayCatalogTasks = useMemo(() => {
    const safeFilteredData = filteredData || {};
    return Array.isArray(safeFilteredData['Catalog Tasks']) 
      ? safeFilteredData['Catalog Tasks'].filter(Boolean)
      : [];
  }, [filteredData]);

  console.log('Data being passed to PredictiveAnalysis:', {
    incidentsCount: displayIncidents.length,
    catalogTasksCount: displayCatalogTasks.length,
    activeTab
  });

  const processIncidentsByApi = React.useMemo(() => {
    // Process incidents to find those related to deployments
    const linkedIncidents = displayIncidents
      .filter(incident => {
        const description = incident.short_description || incident.description || "";
        return (typeof description === 'string') && (
          description.toLowerCase().includes('deploy') || 
          description.toLowerCase().includes('api')
        );
      })
      .map(incident => ({
        incident_number: incident.number,
        incident_description: incident.short_description || incident.description,
        incident_created_date: incident.opened || incident.created || incident.opened_at,
        priority: incident.priority,
        status: incident.state || incident.status,
        catalog_task_date: incident.catalog_task_date,
        api_name: incident.api_name || 'Unknown API'
      }));

    // Group by API
    const grouped: Record<string, any[]> = {};
    linkedIncidents.forEach(incident => {
      const apiName = incident.api_name;
      if (!grouped[apiName]) {
        grouped[apiName] = [];
      }
      grouped[apiName].push(incident);
    });

    // Sort by number of incidents (descending)
    return Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);
  }, [displayIncidents]);

  // Get the date range for PredictiveAnalysis
  const startDate = React.useMemo(() => {
    if (dateRange?.from) return dateRange.from;
    const today = new Date();
    return subDays(today, 30); // Default to last 30 days if no date range provided
  }, [dateRange]);

  const endDate = React.useMemo(() => {
    if (dateRange?.to) return dateRange.to;
    return new Date(); // Default to today if no end date provided
  }, [dateRange]);

  console.log('Date range being passed to PredictiveAnalysis:', {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    dateRange
  });

  return (
    <div className="space-y-6">
      {dataSource === 'empty' && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription>
            No ticket data is currently available. Please ensure your data files are properly configured and accessible.
          </AlertDescription>
        </Alert>
      )}
      
      <DashboardStats stats={stats} />
      

      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-3 lg:flex">
          <TabsTrigger value="executive">Executive Summary</TabsTrigger>
          <TabsTrigger value="operational">Operational Metrics</TabsTrigger>
          <TabsTrigger value="business">Resource Allocation</TabsTrigger>
          {/* <TabsTrigger value="predictive">Predictive Analysis</TabsTrigger> */}
          {/* <TabsTrigger value="deployment-patterns">Deployment Patterns</TabsTrigger> */}
          {/* <TabsTrigger value="predictive-features">Predictive Features</TabsTrigger> */}
        </TabsList>
        
        <TabsContent value="executive" className="space-y-6">
          <ExecutiveSummary incidents={displayIncidents} catalogTasks={displayCatalogTasks} />
          
          <AvgResolutionTime incidents={displayIncidents} catalogTasks={displayCatalogTasks} />
          
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <div className="space-y-6">
            
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="operational" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* <WeeklyTicketVolume 
              incidents={filteredData['Incidents'] || []} 
              catalogTasks={filteredData['Catalog Tasks'] || []} 
            />
            <DailyTicketVolume 
              incidents={filteredData['Incidents'] || []} 
              catalogTasks={filteredData['Catalog Tasks'] || []} 
            /> */}
          </div>
          
          {/* Weekday vs Weekend Analytics */}
          <WeekdayWeekendAnalytics
            incidents={filteredData['Incidents'] || []}
            catalogTasks={filteredData['Catalog Tasks'] || []}
            dateRange={dateRange}
          />
          
          {/* 24-Hour Distribution Analytics */}
          <HourlyDistributionAnalytics
            incidents={filteredData['Incidents'] || []}
            catalogTasks={filteredData['Catalog Tasks'] || []}
            dateRange={dateRange}
          />
          
          {/* Single Date Analysis Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Single Date Analysis</span>
                <div className="flex items-center gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal w-[200px]",
                          !selectedSingleDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedSingleDate ? format(selectedSingleDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={selectedSingleDate}
                        onSelect={(date) => {
                          if (date) {
                            setSelectedSingleDate(date);
                            setShowHourlyChart(true);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <Button
                    variant={showHourlyChart ? "default" : "outline"}
                    onClick={() => setShowHourlyChart(!showHourlyChart)}
                  >
                    {showHourlyChart ? "Hide" : "Show"} Hourly View
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            {showHourlyChart && (
              <CardContent>
                <HourlyTicketChart
                  incidents={filteredData['Incidents'] || []}
                  catalogTasks={filteredData['Catalog Tasks'] || []}
                  selectedDate={selectedSingleDate}
                />
              </CardContent>
            )}
          </Card>
          
          {/* <RootCauseAnalysis incidents={displayIncidents} /> */}
          
          <RepetitiveTicketsChart data={repetitiveTicketsData} />
        </TabsContent>
        
        <TabsContent value="business" className="space-y-6">
          <ResourceAllocationChart 
            incidents={displayIncidents}
            catalogTasks={displayCatalogTasks}
          />
        </TabsContent>
        
        <TabsContent value="predictive" className="space-y-6">
          {dataLoaded ? (
            <AIPredictiveAnalysis 
              incidents={displayIncidents}
              catalogTasks={displayCatalogTasks}
              startDate={startDate}
              endDate={endDate}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Loading Data</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-gray-500">
                  Loading data for AI analysis...
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* <TabsContent value="deployment-patterns" className="space-y-6">
          <DeploymentPatternAnalysis 
            incidents={displayIncidents}
            catalogTasks={displayCatalogTasks}
            dateRange={dateRange}
          />
        </TabsContent> */}

        <TabsContent value="predictive-features">
          <PredictiveFeatures dateRange={dateRange} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
